import { useContext, useEffect, useState } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { API_URL } from "../../Constants";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ClickToCall = () => {
  const { user } = useContext(AuthContext);
  const [callApiKey, setCallApiKey] = useState("");
  const [existing, setExisting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const companyId =
    user?.accountType === "COMPANY" ? user?.id : user?.companyId;

  useEffect(() => {
    if (companyId) {
      fetchApiKey();
    }
  }, [companyId]);

  const fetchApiKey = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/call-api-key/${companyId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (res.data?.callApiKey) {
        setCallApiKey(res.data.callApiKey);
        setExisting(true);
      } else {
        setCallApiKey("");
        setExisting(false);
      }
    } catch (error) {
      setCallApiKey("");
      setExisting(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/call-api-key`,
        { callApiKey, companyId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success("API Key saved successfully.");
      setExisting(true);
    } catch (error) {
      toast.error("Failed to save API Key.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/call-api-key/${companyId}`,
        { callApiKey: "", companyId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success("API Key deleted successfully.");
      setCallApiKey("");
      setExisting(false);
      setShowDeleteModal(false);
    } catch (error) {
      toast.error("Failed to delete API Key.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto bg-[#FAF8FF] dark:text-white dark:bg-gray-900 p-6 rounded-lg shadow-lg cfg-font">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700&family=Inter:wght@400;500;600&display=swap'); .cfg-heading{font-family:'Sora',sans-serif;} .cfg-font{font-family:'Inter',sans-serif;}`}</style>
      <h2 className="text-xl font-bold cfg-heading text-[#1F2937] dark:text-white mb-4">
        Click To Call API Key Configuration
      </h2>

      <input
        type="text"
        placeholder="Enter API Key"
        value={callApiKey}
        onChange={(e) => setCallApiKey(e.target.value)}
        className="w-full p-2 border border-[#E5E7EB] rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#E9D5FF] focus:border-[#673AB7]"
      />

      {/* Preview Info */}
      <div className="mb-2">
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="text-[#673AB7] hover:underline"
        >
          {showPreview ? "Hide" : "Preview"} API Key Info
        </button>

        {showPreview && (
          <div className="mt-2 p-4 bg-gray-100 dark:bg-gray-700 text-sm text-gray-800 dark:text-white rounded border border-gray-300 dark:border-gray-600">
            <strong>Instructions:</strong>
            <ol className="list-decimal list-inside space-y-2 text-sm">
              <li>Obtain your API key from your IVR provider</li>
              <li>Paste it in the input field above</li>
              <li>Click Save to store your key securely</li>
              <li>You can delete or update it anytime</li>
            </ol>
          </div>
        )}
      </div>

      <div className="flex space-x-4">
        <button
          onClick={handleSave}
          disabled={loading || !callApiKey}
          className="bg-[#673AB7] hover:bg-[#5E35A6] text-white px-4 py-2 rounded disabled:opacity-50"
        >
          {existing ? "Update Key" : "Save Key"}
        </button>

        {existing && (
          <button
            onClick={() => setShowDeleteModal(true)}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
          >
            Delete Key
          </button>
        )}
      </div>

      {loading && (
        <div className="mt-2 text-sm text-gray-500">Processing...</div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-sm w-full">
            <h3 className="text-lg font-bold mb-4 cfg-heading text-[#1F2937] dark:text-white">
              Confirm Delete
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Are you sure you want to delete this API Key?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClickToCall;
